import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, ClipboardCopy, RefreshCw, UserPlus, Truck, Users, Baby } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const BUS_LIST = Array.from({ length: 34 }, (_, i) => `Bus #${String(i + 1).padStart(2, "0")}`);

const formatTimestamp = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
};

/* -------------------------------------------------------------------------
 * Camper Submissions Dialog
 * ------------------------------------------------------------------------- */
export const CamperFormsDialog = ({ open, onOpenChange, uniqueBuses = [], onAfterChange }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const endpoint = `${API}/forms/camper`;

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/forms/submissions?form_type=camper`);
      setSubmissions(res.data.submissions || []);
    } catch {
      toast.error("Failed to load camper submissions");
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this submission from the audit log? (Camper record stays)")) return;
    try {
      await axios.delete(`${API}/forms/submissions/${id}`);
      toast.success("Submission removed");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="camper-forms-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Baby className="w-5 h-5" />
            Camper Transportation Forms
          </DialogTitle>
          <DialogDescription>
            Submissions from the CampFront camper form. Each one is auto-zoned and added to the
            active season immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-3 text-xs space-y-1">
          <div className="font-semibold text-slate-700">CampFront webhook URL — POST to:</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border px-2 py-1 rounded break-all">{endpoint}</code>
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(endpoint)} data-testid="copy-camper-endpoint">
              <ClipboardCopy className="w-3 h-3" />
            </Button>
          </div>
          <div className="text-slate-600">
            Required: <code>first_name</code>, <code>last_name</code>, <code>address</code>,{" "}
            <code>town</code>, <code>zip_code</code>. Optional: <code>person_id</code>,{" "}
            <code>dob</code>, <code>session</code>, <code>am_bus_needed</code>,{" "}
            <code>pm_bus_needed</code>, <code>pickup_dropoff</code>.
          </div>
        </div>

        <div className="flex justify-between items-center mb-2">
          <div className="text-sm text-gray-600">{submissions.length} submissions</div>
          <Button size="sm" variant="outline" onClick={fetchData} data-testid="refresh-camper-submissions">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {submissions.length === 0 ? (
            <div className="text-center text-gray-500 py-8 text-sm">No camper submissions yet.</div>
          ) : (
            submissions.map((s) => {
              const d = s.data || {};
              const r = s.resolution || {};
              return (
                <div
                  key={s.id}
                  className="border rounded p-3 bg-blue-50/30 flex justify-between gap-3"
                  data-testid={`camper-submission-${s.id}`}
                >
                  <div className="flex-1 text-sm">
                    <div className="font-semibold">
                      {d.first_name} {d.last_name}
                      {d.dob && <span className="text-gray-500 ml-2 text-xs">DOB: {d.dob}</span>}
                      {d.person_id && <span className="text-gray-500 ml-2 text-xs">ID: {d.person_id}</span>}
                    </div>
                    <div className="text-xs text-gray-600">
                      {d.address}, {d.town} {d.zip_code}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Parent: {d.parent_name || "—"} · {d.parent_phone || d.parent_email || "no contact"}
                    </div>
                    <div className="text-xs mt-1 flex gap-2 flex-wrap">
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                        Session: {d.session || "—"}
                      </span>
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                        AM: {d.am_bus_needed ? "Yes" : "No"} · PM: {d.pm_bus_needed ? "Yes" : "No"}
                      </span>
                      {r.matched_zone_bus && (
                        <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                          Zone → {r.matched_zone_bus}
                        </span>
                      )}
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
                        Action: {r.action || "—"}
                      </span>
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1">{formatTimestamp(s.submitted_at)}</div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-500"
                      onClick={() => handleDelete(s.id)}
                      data-testid={`delete-camper-submission-${s.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------------------------------------------------------------
 * Staff Submissions Dialog (Needs Transportation queue)
 * ------------------------------------------------------------------------- */
export const StaffFormsDialog = ({ open, onOpenChange, uniqueBuses = [], onAfterChange }) => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const endpoint = `${API}/forms/staff`;

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/forms/staff-queue`);
      setStaff(res.data.staff || []);
    } catch {
      toast.error("Failed to load staff submissions");
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAssignBus = async (id, busNumber) => {
    try {
      await axios.put(`${API}/staff-addresses/${id}`, { bus_number: busNumber });
      toast.success(`Assigned to ${busNumber}`);
      fetchData();
      if (onAfterChange) onAfterChange();
    } catch {
      toast.error("Failed to assign bus");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the staff list?`)) return;
    try {
      await axios.delete(`${API}/staff-addresses/${id}`);
      toast.success(`Removed ${name}`);
      fetchData();
      if (onAfterChange) onAfterChange();
    } catch {
      toast.error("Failed to remove");
    }
  };

  const busList = (uniqueBuses && uniqueBuses.length > 0)
    ? uniqueBuses.filter((b) => b.startsWith("Bus"))
    : BUS_LIST;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="staff-forms-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Users className="w-5 h-5" />
            Staff Transportation — Needs Transportation Queue
          </DialogTitle>
          <DialogDescription>
            Staff who submitted the transportation form. Assign each to a bus &mdash; they&apos;ll render as a
            triangle on the map at their home address.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-3 text-xs space-y-1">
          <div className="font-semibold text-slate-700">CampFront webhook URL — POST to:</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border px-2 py-1 rounded break-all">{endpoint}</code>
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(endpoint)} data-testid="copy-staff-endpoint">
              <ClipboardCopy className="w-3 h-3" />
            </Button>
          </div>
          <div className="text-slate-600">
            Required: <code>first_name</code>, <code>last_name</code>, <code>role</code>,{" "}
            <code>home_address</code>. Conditional: <code>has_cdl</code>, <code>cdl_class</code>,{" "}
            <code>is_counselor</code>, <code>needs_seat</code>, <code>is_carpooling</code>,{" "}
            <code>carpool_partner_name</code>, <code>phone</code>, <code>session</code>.
          </div>
        </div>

        <div className="flex justify-between items-center mb-2">
          <div className="text-sm text-gray-600">{staff.length} staff in queue</div>
          <Button size="sm" variant="outline" onClick={fetchData} data-testid="refresh-staff-submissions">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {staff.length === 0 ? (
            <div className="text-center text-gray-500 py-8 text-sm">No staff submissions yet.</div>
          ) : (
            staff.map((s) => (
              <div
                key={s.id}
                className="border rounded p-3 bg-amber-50/30 flex justify-between gap-3 items-start"
                data-testid={`staff-submission-${s.id}`}
              >
                <div className="flex-1 text-sm">
                  <div className="font-semibold flex items-center gap-2">
                    {s.name}
                    {s.has_cdl && (
                      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold">
                        CDL {s.cdl_class || ""}
                      </span>
                    )}
                    {s.is_counselor && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">
                        Counselor
                      </span>
                    )}
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] uppercase">
                      {s.role}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5">{s.address}</div>
                  <div className="text-xs text-gray-500">
                    {s.email && <span>{s.email}</span>}
                    {s.phone && <span className="ml-2">{s.phone}</span>}
                  </div>
                  <div className="text-xs mt-1 flex gap-2 flex-wrap">
                    {s.needs_seat && (
                      <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">
                        Needs Seat
                      </span>
                    )}
                    {s.is_carpooling && (
                      <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                        Carpooling{s.carpool_partner_name ? ` w/ ${s.carpool_partner_name}` : ""}
                      </span>
                    )}
                    {s.zone_info?.bus_number && (
                      <span className="px-1.5 py-0.5 bg-green-100 text-green-700 rounded">
                        Zone → {s.zone_info.bus_number}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1 min-w-[180px]">
                  <Select
                    value={s.bus_number || ""}
                    onValueChange={(val) => handleAssignBus(s.id, val)}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid={`staff-assign-${s.id}`}>
                      <SelectValue placeholder="Assign Bus" />
                    </SelectTrigger>
                    <SelectContent>
                      {busList.map((b) => (
                        <SelectItem key={b} value={b}>
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-red-500"
                    onClick={() => handleDelete(s.id, s.name)}
                    data-testid={`delete-staff-submission-${s.id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* -------------------------------------------------------------------------
 * Sub Counselor Management Dialog
 * ------------------------------------------------------------------------- */
export const SubCounselorDialog = ({ open, onOpenChange, uniqueBuses = [] }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newSub, setNewSub] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    address: "", town: "", zip_code: "", availability_notes: "", has_experience: false, notes: "",
  });
  const endpoint = `${API}/forms/sub-counselor`;

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sub-counselors`);
      setSubs(res.data.sub_counselors || []);
    } catch {
      toast.error("Failed to load sub counselors");
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = async () => {
    if (!newSub.first_name || !newSub.last_name || !newSub.address) {
      toast.error("First name, last name, and address are required");
      return;
    }
    try {
      await axios.post(endpoint, newSub);
      toast.success("Sub counselor added");
      setNewSub({
        first_name: "", last_name: "", phone: "", email: "",
        address: "", town: "", zip_code: "", availability_notes: "", has_experience: false, notes: "",
      });
      setAdding(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add");
    }
  };

  const handleAssignBus = async (id, busNumber) => {
    try {
      await axios.put(`${API}/sub-counselors/${id}`, { assigned_bus_number: busNumber });
      toast.success(busNumber ? `Assigned to ${busNumber}` : "Unassigned");
      fetchData();
    } catch {
      toast.error("Failed to assign");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    try {
      await axios.delete(`${API}/sub-counselors/${id}`);
      toast.success(`Removed ${name}`);
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const busList = (uniqueBuses && uniqueBuses.length > 0)
    ? uniqueBuses.filter((b) => b.startsWith("Bus"))
    : BUS_LIST;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sub-counselor-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Sub Counselors
          </DialogTitle>
          <DialogDescription>
            Substitute counselors available to assign to a bus when the regular counselor is out.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded p-3 mb-3 text-xs space-y-1">
          <div className="font-semibold text-slate-700">CampFront webhook URL — POST to:</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white border px-2 py-1 rounded break-all">{endpoint}</code>
            <Button size="sm" variant="outline" onClick={() => copyToClipboard(endpoint)} data-testid="copy-sub-endpoint">
              <ClipboardCopy className="w-3 h-3" />
            </Button>
          </div>
        </div>

        <div className="flex justify-between items-center mb-2">
          <div className="text-sm text-gray-600">{subs.length} sub counselors</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={fetchData} data-testid="refresh-subs">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
            <Button size="sm" onClick={() => setAdding(!adding)} data-testid="toggle-add-sub">
              <UserPlus className="w-4 h-4 mr-1" /> {adding ? "Cancel" : "Add Sub"}
            </Button>
          </div>
        </div>

        {adding && (
          <div className="border rounded p-3 bg-blue-50 space-y-2 mb-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">First Name *</Label>
                <Input
                  className="h-8 text-sm"
                  value={newSub.first_name}
                  onChange={(e) => setNewSub({ ...newSub, first_name: e.target.value })}
                  data-testid="new-sub-first-name"
                />
              </div>
              <div>
                <Label className="text-xs">Last Name *</Label>
                <Input
                  className="h-8 text-sm"
                  value={newSub.last_name}
                  onChange={(e) => setNewSub({ ...newSub, last_name: e.target.value })}
                  data-testid="new-sub-last-name"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Address *</Label>
              <Input
                className="h-8 text-sm"
                value={newSub.address}
                onChange={(e) => setNewSub({ ...newSub, address: e.target.value })}
                data-testid="new-sub-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Town</Label>
                <Input
                  className="h-8 text-sm"
                  value={newSub.town}
                  onChange={(e) => setNewSub({ ...newSub, town: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Zip</Label>
                <Input
                  className="h-8 text-sm"
                  value={newSub.zip_code}
                  onChange={(e) => setNewSub({ ...newSub, zip_code: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input
                  className="h-8 text-sm"
                  value={newSub.phone}
                  onChange={(e) => setNewSub({ ...newSub, phone: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input
                  className="h-8 text-sm"
                  value={newSub.email}
                  onChange={(e) => setNewSub({ ...newSub, email: e.target.value })}
                />
              </div>
            </div>
            <Button size="sm" className="w-full" onClick={handleAdd} data-testid="save-new-sub">
              Save Sub Counselor
            </Button>
          </div>
        )}

        <div className="space-y-2 max-h-[55vh] overflow-y-auto">
          {subs.length === 0 ? (
            <div className="text-center text-gray-500 py-8 text-sm">No sub counselors yet.</div>
          ) : (
            subs.map((s) => (
              <div
                key={s.id}
                className="border rounded p-3 bg-emerald-50/30 flex justify-between gap-3 items-start"
                data-testid={`sub-counselor-${s.id}`}
              >
                <div className="flex-1 text-sm">
                  <div className="font-semibold">
                    {s.name}
                    {s.has_experience && (
                      <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px]">
                        Experienced
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600">{s.address}</div>
                  <div className="text-xs text-gray-500">
                    {s.phone && <span>{s.phone}</span>}
                    {s.email && <span className="ml-2">{s.email}</span>}
                  </div>
                  {s.availability_notes && (
                    <div className="text-xs italic text-gray-500 mt-1">📅 {s.availability_notes}</div>
                  )}
                  {s.nearest_zone_bus && (
                    <div className="text-xs mt-1">
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                        Nearest zone: {s.nearest_zone_bus}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 min-w-[180px]">
                  <Select
                    value={s.assigned_bus_number || ""}
                    onValueChange={(val) => handleAssignBus(s.id, val)}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid={`sub-assign-${s.id}`}>
                      <SelectValue placeholder="Assign Bus" />
                    </SelectTrigger>
                    <SelectContent>
                      {busList.map((b) => (
                        <SelectItem key={b} value={b}>
                          {b}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {s.assigned_bus_number && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => handleAssignBus(s.id, "")}
                      data-testid={`sub-unassign-${s.id}`}
                    >
                      Unassign
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-red-500"
                    onClick={() => handleDelete(s.id, s.name)}
                    data-testid={`delete-sub-${s.id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
